"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GameClient = void 0;
const game_1 = require("game");
const WebSocket = require("isomorphic-ws");
const events_1 = require("events");
const ClickUpdate_1 = require("game/dist/core/ClickUpdate");
class GameClient {
    constructor(server) {
        this.events = new events_1.EventEmitter();
        this.chunkUpdateListener = new Map();
        this.clickListener = new Map();
        this.topListener = {};
        this.socket = new WebSocket(server);
        this.socket.onmessage = event => {
            const message = GameClient.readMessage(event);
            if (!message.type) {
                console.log('[GameClient] Received message without type');
                return;
            }
            switch (message.type) {
                case "get_chunk_response":
                    this.events.emit(`chunk_response_${message.data.chunk.x}_${message.data.chunk.y}`, message);
                    break;
                case "register_chunk_listener_response":
                    this.events.emit(`chunk_listener_response_${message.chunk.x}_${message.chunk.y}`, message);
                    break;
                case "chunk_update":
                    const token = message.data.token;
                    const listener = this.chunkUpdateListener.get(token);
                    if (listener) {
                        listener(new game_1.ChunkUpdate(game_1.Vector2.copy(message.data.chunk), message.data.contents, game_1.Vector2.copy(message.data.size)));
                    }
                    break;
                case "chunk_size_response":
                    this.events.emit('chunk_size_response', message);
                    break;
                case "login_response":
                    this.events.emit('login_response', message);
                    break;
                case "click_listener_register_response":
                    this.events.emit(`click_listener_register_response_${message.chunk.x}_${message.chunk.y}`, message);
                    break;
                case "click_message":
                    const clickToken = message.token;
                    const clickListener = this.clickListener.get(clickToken);
                    if (clickListener) {
                        clickListener(new ClickUpdate_1.ClickUpdate(new game_1.ChunkedPosition(game_1.Vector2.copy(message.chunk), game_1.Vector2.copy(message.position), game_1.Vector2.copy(message.size)), message.user));
                    }
                    break;
                case "register_top_list_listener_response":
                    this.events.emit('register_top_list_listener_response', message);
                    break;
                case "top_message":
                    Object.values(this.topListener).forEach(cb => cb(message.entries));
                    break;
                default:
                    console.log('[GameClient] Received unknown message', message);
            }
        };
    }
    waitForConnection() {
        return new Promise(resolve => {
            this.socket.onopen = () => resolve();
        });
    }
    isClosed() {
        return this.socket.readyState === WebSocket.CLOSED;
    }
    async logIn(secret) {
        const loginRequest = {
            type: "login_request",
            secret: secret,
        };
        this.socket.send(JSON.stringify(loginRequest));
        return await this.waitForEvent('login_response');
    }
    async flag(_, position) {
        const flagRequest = {
            type: "flag_request",
            position
        };
        this.socket.send(JSON.stringify(flagRequest));
    }
    async getChunkSize() {
        if (this.chunkSizeCache) {
            return this.chunkSizeCache;
        }
        const chunkSizeRequest = {
            type: "chunk_size_request"
        };
        this.socket.send(JSON.stringify(chunkSizeRequest));
        const response = await this.waitForEvent('chunk_size_response');
        this.chunkSizeCache = game_1.Vector2.copy(response.size);
        return this.chunkSizeCache;
    }
    async getTileContents(_, chunk) {
        const tileContentMessage = {
            type: "get_chunk_request",
            data: chunk
        };
        this.socket.send(JSON.stringify(tileContentMessage));
        const response = await this.waitForEvent(`chunk_response_${chunk.x}_${chunk.y}`);
        return response.data.content.map(elem => {
            if (elem === 'c') {
                return 'closed';
            }
            if (elem === 'f') {
                return 'flag';
            }
            if (elem === 'm') {
                return 'mine';
            }
            return elem;
        });
    }
    async on(ctx, type, chunk, callback) {
        switch (type) {
            case "update":
                return await this.onUpdate(ctx, chunk, callback);
            case "click":
                return await this.onClick(ctx, chunk, callback);
        }
    }
    async onUpdate(_, chunk, callback) {
        const listenerRequest = {
            type: "register_chunk_listener",
            chunkPosition: chunk
        };
        this.socket.send(JSON.stringify(listenerRequest));
        const response = await this.waitForEvent(`chunk_listener_response_${chunk.x}_${chunk.y}`);
        this.chunkUpdateListener.set(response.token, callback);
        return response.token;
    }
    async onClick(_, chunk, callback) {
        const listenerRequest = {
            type: 'click_listener_register_request',
            chunk: chunk,
        };
        this.socket.send(JSON.stringify(listenerRequest));
        const response = await this.waitForEvent(`click_listener_register_response_${chunk.x}_${chunk.y}`);
        this.clickListener.set(response.token, callback);
        return response.token;
    }
    async onTopList(_, callback) {
        this.socket.send(JSON.stringify({
            type: 'register_top_list_listener_request',
        }));
        const response = await this.waitForEvent('register_top_list_listener_response');
        this.topListener[response.token] = callback;
        return response.token;
    }
    async removeTopListener(_, token) {
        this.socket.send(JSON.stringify({
            type: 'unregister_top_list_listener_request',
            token: token,
        }));
        delete this.topListener[token];
    }
    async openTile(_, position) {
        const openRequest = {
            type: "open_request",
            position
        };
        console.log('[GameClient] sending', openRequest);
        this.socket.send(JSON.stringify(openRequest));
    }
    async removeListener(_, token) {
        const removeListenerRequest = {
            type: "remove_chunk_listener",
            token
        };
        this.socket.send(JSON.stringify(removeListenerRequest));
    }
    async removeClickListener(_, token) {
        const removeListenerRequest = {
            type: 'click_listener_unregister_request',
            token: token,
        };
        this.socket.send(JSON.stringify(removeListenerRequest));
    }
    static readMessage(event) {
        let strContent;
        if (event.data instanceof Buffer) {
            strContent = event.data.toString();
        }
        else if (event.data instanceof ArrayBuffer) {
            strContent = String.fromCharCode.apply(null, [...new Uint16Array(event.data)]);
        }
        else {
            strContent = event.data;
        }
        const message = JSON.parse(strContent);
        if (!message.type) {
            throw new Error("Could not detect message type");
        }
        return message;
    }
    waitForEvent(event) {
        return new Promise(resolve => {
            this.events.once(event, (arg) => {
                resolve(arg);
            });
        });
    }
}
exports.GameClient = GameClient;
//# sourceMappingURL=GameClient.js.map